/**
 * Create-project dialog (§7g Commit D).
 *
 * Simple name + description form. Validates against the backend limits
 * (name required 1-200 chars, description 0-2000 chars) before
 * submitting so users don't wait on a round trip to see "name required".
 *
 * On success, pushes a toast, clears the form, and calls onCreated with
 * the new project so the caller can navigate into it.
 */

import { useState } from "react";
import Modal from "./Modal.jsx";
import ErrorBubble from "./ErrorBubble.jsx";
import { useStore } from "../store/useStore.js";

const NAME_MAX = 200;
const DESCRIPTION_MAX = 2000;
const COMPANY_URL_MAX = 2048;

export default function CreateProjectModal({ open, onClose, onCreated }) {
  const createProject = useStore((s) => s.createProject);
  const pushToast = useStore((s) => s.pushToast);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setName("");
    setDescription("");
    setCompanyUrl("");
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name is required");
      return;
    }
    if (trimmedName.length > NAME_MAX) {
      setError(`Name must be ${NAME_MAX} characters or fewer`);
      return;
    }
    if (description.length > DESCRIPTION_MAX) {
      setError(`Description must be ${DESCRIPTION_MAX} characters or fewer`);
      return;
    }
    let trimmedCompanyUrl = companyUrl.trim();
    if (!trimmedCompanyUrl) {
      setError("Company URL is required");
      return;
    }
    if (
      !trimmedCompanyUrl.startsWith("http://") &&
      !trimmedCompanyUrl.startsWith("https://")
    ) {
      trimmedCompanyUrl = `https://${trimmedCompanyUrl}`;
    }
    if (trimmedCompanyUrl.length > COMPANY_URL_MAX) {
      setError(`Company URL must be ${COMPANY_URL_MAX} characters or fewer`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const project = await createProject({
        name: trimmedName,
        description: description.trim(),
        companyUrl: trimmedCompanyUrl,
      });
      pushToast(`Project "${project.name}" created`, "success");
      // Hand off to the caller (which typically navigates away,
      // unmounting the modal) BEFORE resetting local state, so the
      // form keeps its contents until the navigation commits.
      onCreated?.(project);
      reset();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Create a project">
      <form onSubmit={handleSubmit}>
        {error && <ErrorBubble message={error} />}

        <div className="form-field">
          <label htmlFor="new-project-name">Name</label>
          <input
            id="new-project-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Corp"
            maxLength={NAME_MAX}
            required
            autoFocus
            disabled={submitting}
          />
          <span className="form-field-hint">
            A short label — usually a prospect or customer name.
          </span>
        </div>

        <div className="form-field">
          <label htmlFor="new-project-company-url">Company URL</label>
          <input
            id="new-project-company-url"
            type="url"
            value={companyUrl}
            onChange={(e) => setCompanyUrl(e.target.value)}
            placeholder="https://acme.example"
            maxLength={COMPANY_URL_MAX}
            required
            disabled={submitting}
          />
          <span className="form-field-hint">
            The prospect&apos;s homepage — used by the GTM agent&apos;s research pipeline.
          </span>
        </div>

        <div className="form-field">
          <label htmlFor="new-project-description">Description (optional)</label>
          <textarea
            id="new-project-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Cloud security migration — evaluating EDR vendors"
            rows={3}
            maxLength={DESCRIPTION_MAX}
            disabled={submitting}
          />
          <span className="form-field-hint">
            Context for your future self. You can edit this later.
          </span>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !name.trim() || !companyUrl.trim()}
          >
            {submitting ? "Creating…" : "Create project"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
